import { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Paper, Text, Skeleton, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type DateRange } from "../../components/reports/ReportDateRangePicker";
import { useCptFrequencyReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { formatLabDateTime } from "../../utils/labTime";
import {
  usePathologists,
  usePhysicians,
  useSurgeryCenters,
  useBillingScenarios,
} from "../../hooks/useReferenceData";
import { usePractices } from "../../hooks/admin/usePractices";
import { listPrefixes } from "../../services/prefixService";
import { reportsService } from "../../services/reports.service";
import { CptFrequencyFilterPanel } from "./CptFrequencyFilterPanel";
import { CptFrequencyTabs } from "./CptFrequencyTabs";
import { useCptFrequencyReportStore } from "../../store/cptFrequencyReport.store";
import type {
  BillingReportAccountType,
  BillingReportDateBasis,
} from "../../services/reports.service";

export function CptFrequencyReportContent() {
  const labToday = useLabTodayDateOnly();
  const [searchParams] = useSearchParams();
  const [urlFiltersReady, setUrlFiltersReady] = useState(false);
  const {
    dateRange,
    dateBasis,
    accountType,
    pathologistId,
    practiceId,
    physicianId,
    surgeryCenterLabId,
    prefixId,
    billingScenarioId,
    page,
    updateFilters,
    setPage,
  } = useCptFrequencyReportStore();

  const { data: pathologists = [] } = usePathologists();
  const { data: practices = [] } = usePractices();
  const { data: physicians = [] } = usePhysicians();
  const { data: surgeryCenters = [] } = useSurgeryCenters();
  const { data: billingScenarios = [] } = useBillingScenarios();

  const { data: prefixListResponse } = useQuery({
    queryKey: ["prefixes", "all"],
    queryFn: () => listPrefixes({ all: true }),
  });
  const prefixes = useMemo(
    () => prefixListResponse?.data ?? [],
    [prefixListResponse]
  );

  const prefixOptions = useMemo(
    () => prefixes.map((p) => ({ value: p.id, label: p.code })),
    [prefixes]
  );

  const billingScenarioOptions = billingScenarios;

  const pathologistOptions = useMemo(
    () => pathologists.map((p) => ({ value: p.id, label: p.name })),
    [pathologists]
  );
  const practiceOptions = useMemo(
    () => practices.map((p) => ({ value: p.id, label: p.name })),
    [practices]
  );

  const applyUrlParams = useCallback(() => {
    const urlPathologistId = searchParams.get("pathologistId");
    const urlStart = searchParams.get("startDate");
    const urlEnd = searchParams.get("endDate");
    const updates: { pathologistId?: string; dateRange?: DateRange } = {};
    if (urlPathologistId) updates.pathologistId = urlPathologistId;
    if (urlStart && urlEnd) {
      const start = new Date(urlStart);
      const end = new Date(urlEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        updates.dateRange = { startDate: start, endDate: end };
      }
    }
    if (Object.keys(updates).length > 0) updateFilters(updates);
    setUrlFiltersReady(true);
  }, [searchParams, updateFilters]);

  useEffect(() => {
    applyUrlParams();
  }, [applyUrlParams]);

  const params = useMemo(
    () => ({
      startDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
      dateBasis,
      accountTypes: [accountType],
      pathologistId: pathologistId ?? undefined,
      practiceId: practiceId ?? undefined,
      physicianId: physicianId ?? undefined,
      surgeryCenterLabId: surgeryCenterLabId ?? undefined,
      prefixId: prefixId ?? undefined,
      billingScenarioId: billingScenarioId ?? undefined,
    }),
    [
      dateRange,
      dateBasis,
      accountType,
      pathologistId,
      practiceId,
      physicianId,
      surgeryCenterLabId,
      prefixId,
      billingScenarioId,
    ]
  );

  const { data, isLoading, isFetching, isError } = useCptFrequencyReport(
    params,
    { enabled: urlFiltersReady }
  );

  const totalPages = useMemo(
    () =>
      data
        ? Math.ceil(
            (data.allByCode?.length ?? data.pagination.total) /
              data.pagination.limit
          )
        : 1,
    [data]
  );
  const visibleCodeRows = useMemo(() => {
    if (!data) return [];
    const allByCode = data.allByCode ?? data.byCode;
    const start = (page - 1) * data.pagination.limit;
    return allByCode.slice(start, start + data.pagination.limit);
  }, [data, page]);

  const exportParams = useMemo(
    () => ({
      ...params,
      reportRunId: data?.reportRunId,
    }),
    [params, data?.reportRunId]
  );

  const handleDateRangeChange = useCallback(
    (r: DateRange) => {
      updateFilters({ dateRange: r });
    },
    [updateFilters]
  );

  const handleExcelExport = useCallback(
    () => reportsService.exportCptFrequencyExcel(exportParams),
    [exportParams]
  );

  const handlePdfExport = useCallback(
    () => reportsService.exportCptFrequencyPdf(exportParams),
    [exportParams]
  );

  const handleDateBasisChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      updateFilters({ dateBasis: value as BillingReportDateBasis });
    },
    [updateFilters]
  );

  const handleAccountTypeChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      updateFilters({ accountType: value as BillingReportAccountType });
    },
    [updateFilters]
  );

  const handlePageChange = useCallback(
    (p: number) => {
      setPage(p);
    },
    [setPage]
  );

  const handlePathologistChange = useCallback(
    (value: string | null) => {
      updateFilters({ pathologistId: value });
    },
    [updateFilters]
  );
  const handlePracticeChange = useCallback(
    (value: string | null) => {
      updateFilters({ practiceId: value });
    },
    [updateFilters]
  );
  const handlePhysicianChange = useCallback(
    (value: string | null) => {
      updateFilters({ physicianId: value });
    },
    [updateFilters]
  );
  const handleSurgeryCenterChange = useCallback(
    (value: string | null) => {
      updateFilters({ surgeryCenterLabId: value });
    },
    [updateFilters]
  );
  const handlePrefixChange = useCallback(
    (value: string | null) => {
      updateFilters({ prefixId: value });
    },
    [updateFilters]
  );
  const handleBillingScenarioChange = useCallback(
    (value: string | null) => {
      updateFilters({ billingScenarioId: value });
    },
    [updateFilters]
  );

  const reportIsLoading = isLoading || isFetching;
  const hasData = useMemo(
    () => Boolean(data) && !isError && !reportIsLoading,
    [data, isError, reportIsLoading]
  );
  const grandTotal = useMemo(() => data?.grandTotal ?? 0, [data]);

  const exportFilenames = useMemo(
    () => ({
      excel: `cpt-frequency-${labToday}.xlsx`,
      pdf: `cpt-frequency-${labToday}.pdf`,
    }),
    [labToday]
  );

  return (
    <Stack gap="lg">
      <CptFrequencyFilterPanel
        dateBasis={dateBasis}
        accountType={accountType}
        dateRange={dateRange}
        pathologistOptions={pathologistOptions}
        practiceOptions={practiceOptions}
        physicianOptions={physicians}
        surgeryCenterOptions={surgeryCenters}
        prefixOptions={prefixOptions}
        billingScenarioOptions={billingScenarioOptions}
        pathologistId={pathologistId}
        practiceId={practiceId}
        physicianId={physicianId}
        surgeryCenterLabId={surgeryCenterLabId}
        prefixId={prefixId}
        billingScenarioId={billingScenarioId}
        excelFilename={exportFilenames.excel}
        pdfFilename={exportFilenames.pdf}
        exportDisabled={!data || reportIsLoading || isError}
        onDateBasisChange={handleDateBasisChange}
        onAccountTypeChange={handleAccountTypeChange}
        onDateRangeChange={handleDateRangeChange}
        onPathologistChange={handlePathologistChange}
        onPracticeChange={handlePracticeChange}
        onPhysicianChange={handlePhysicianChange}
        onSurgeryCenterChange={handleSurgeryCenterChange}
        onPrefixChange={handlePrefixChange}
        onBillingScenarioChange={handleBillingScenarioChange}
        onExcelExport={handleExcelExport}
        onPdfExport={handlePdfExport}
      />

      {!isError && !reportIsLoading && (data?.unassignedCaseCount ?? 0) > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Unassigned billing accounts"
          role="alert"
        >
          {data?.unassignedCaseCount} qualifying case
          {data?.unassignedCaseCount === 1 ? "" : "s"} could not be assigned to
          the selected account type. They remain visible under Unassigned for
          reconciliation.
          <br />
          Cases:{" "}
          {data?.reconciliationCases
            .filter((reportCase) =>
              reportCase.issues.includes("Unassigned billing account")
            )
            .map((reportCase) => reportCase.caseNumber)
            .join(", ")}
        </Alert>
      )}

      {!isError &&
        !reportIsLoading &&
        (data?.backfilledTcCaseCount ?? 0) > 0 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title="TC billing snapshot provenance"
            role="alert"
          >
            {data?.backfilledTcCaseCount} qualifying TC case
            {data?.backfilledTcCaseCount === 1 ? "" : "s"} had billing facts
            initialized during this reporting rollout because the completion
            predates immutable TC snapshots.
            <br />
            Cases:{" "}
            {data?.reconciliationCases
              .filter((reportCase) =>
                reportCase.issues.includes("TC snapshot initialized by report")
              )
              .map((reportCase) => reportCase.caseNumber)
              .join(", ")}
          </Alert>
        )}

      {isError && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Report could not be verified"
          role="alert"
        >
          No billing totals are shown or exportable because the report failed
          its data checks. Retry, or investigate the case data before billing.
        </Alert>
      )}

      {hasData && (
        <Paper p="md" withBorder>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Grand Total
          </Text>
          <Text size="xl" fw={700}>
            {grandTotal}
          </Text>
          <Text size="xs" c="dimmed">
            CPT codes assigned in selected period
          </Text>
          <Text size="xs" c="dimmed">
            Generated{" "}
            {data?.generatedAt ? formatLabDateTime(data.generatedAt) : "—"}
          </Text>
        </Paper>
      )}

      {reportIsLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} h={36} />
          ))}
        </Stack>
      ) : isError ? null : (
        <CptFrequencyTabs
          data={data}
          byCodeRows={visibleCodeRows}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </Stack>
  );
}
